/**
 * Add Floor Plan Modal Component
 * Wizard for importing and configuring floor plan overlays
 *
 * Steps:
 * 1. Select File: PDF/PNG/JPEG picker, page selector for PDFs
 * 2. Floor Info: Name, floor number
 * 3. Processing: Convert and tile the image
 * 4. Align: Embedded alignment tool (happens on map after modal closes)
 */

import { useState, useEffect, useCallback } from 'react';
import type { FloorPlanOverlay, FloorPlanTransform } from '../../../../shared/types/floorplan';
import { createDefaultTransform } from '../../../../shared/types/floorplan';
import { useFilePicker, FILE_ACCEPTS } from '../../../hooks/useFilePicker';
import { api } from '../../../services/api';
import './AddFloorPlanModal.css';

type WizardStep = 'select' | 'info' | 'processing' | 'complete';

interface AddFloorPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (overlay: Omit<FloorPlanOverlay, 'id' | 'createdAt' | 'updatedAt'>) => void;
  /** Default center coordinates for initial placement */
  defaultCenter?: { lng: number; lat: number };
  /** Editing an existing floor plan */
  editingFloorPlan?: FloorPlanOverlay;
}

export function AddFloorPlanModal({
  isOpen,
  onClose,
  onAdd,
  defaultCenter,
  editingFloorPlan,
}: AddFloorPlanModalProps) {
  const { pickFiles } = useFilePicker();

  // Step state
  const [step, setStep] = useState<WizardStep>('select');

  // File selection state
  const [filePath, setFilePath] = useState('');
  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<'pdf' | 'image'>('image');
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);

  // Floor info state
  const [floorName, setFloorName] = useState('');
  const [floorNumber, setFloorNumber] = useState(0);
  const [makeTransparent, setMakeTransparent] = useState(true);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ stage: '', progress: 0, message: '' });
  const [error, setError] = useState<string | null>(null);

  // Result state
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [tileCache, setTileCache] = useState<string | null>(null);
  const [tileMetadata, setTileMetadata] = useState<any>(null);

  const isEditing = !!editingFloorPlan;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editingFloorPlan) {
        // Populate for editing
        setFilePath(editingFloorPlan.sourcePath);
        setFileName(editingFloorPlan.sourcePath.split(/[\\/]/).pop() || '');
        setFileType(editingFloorPlan.sourceType === 'pdf' ? 'pdf' : 'image');
        setSelectedPage(editingFloorPlan.sourcePage || 1);
        setFloorName(editingFloorPlan.name);
        setFloorNumber(editingFloorPlan.floorNumber);
        setImageWidth(editingFloorPlan.imageWidth || 0);
        setImageHeight(editingFloorPlan.imageHeight || 0);
        setTileCache(editingFloorPlan.tileCache || null);
        setTileMetadata(editingFloorPlan.tileMetadata || null);
        setStep('info');
      } else {
        // Reset for new floor plan
        setFilePath('');
        setFileName('');
        setSelectedFile(null);
        setFileType('image');
        setPdfPageCount(0);
        setSelectedPage(1);
        setFloorName('');
        setFloorNumber(0);
        setMakeTransparent(true);
        setIsProcessing(false);
        setProgress({ stage: '', progress: 0, message: '' });
        setError(null);
        setImageWidth(0);
        setImageHeight(0);
        setTileCache(null);
        setTileMetadata(null);
        setStep('select');
      }
    }
  }, [isOpen, editingFloorPlan]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isProcessing) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isProcessing, onClose]);

  // Handle file selection
  const handleSelectFile = async () => {
    const result = await pickFiles({
      accept: FILE_ACCEPTS.FLOOR_PLAN,
    });

    if (result.canceled || !result.files[0]) return;

    const file = result.files[0];
    const name = file.name;
    const ext = name.split('.').pop()?.toLowerCase() || '';

    setSelectedFile(file);
    setFilePath(name);
    setFileName(name);
    setError(null);

    // Auto-fill floor name from filename
    const baseName = name.replace(/\.[^.]+$/, '');
    if (!floorName) {
      setFloorName(baseName);
    }

    if (ext === 'pdf') {
      setFileType('pdf');
      // For PDF page count, we upload first then check server-side
      // For now, default to 1 page - the server will handle multi-page PDFs
      setPdfPageCount(1);
      setSelectedPage(1);
    } else {
      setFileType('image');
      setPdfPageCount(0);
    }
  };

  // Process the floor plan (convert and tile)
  const handleProcess = async () => {
    if (!selectedFile && !filePath) return;

    setIsProcessing(true);
    setError(null);
    setStep('processing');

    try {
      // Upload the file first if we have a File object
      let serverFilePath = filePath;
      if (selectedFile) {
        const uploaded = await api.file.upload(selectedFile);
        serverFilePath = uploaded.storagePath;
        setFilePath(serverFilePath);
      }

      // Step 1: Convert to PNG (if PDF) or process image
      const sourceType = fileType === 'pdf' ? 'pdf' : 'image';
      const convertResult: any = await api.floorPlan.convert(
        serverFilePath,
        sourceType,
        fileType === 'pdf' ? selectedPage : undefined,
        300, // DPI for PDF rendering
        makeTransparent,
      );

      if (!convertResult.success) {
        throw new Error(convertResult.error || 'Conversion failed');
      }

      setImageWidth(convertResult.width!);
      setImageHeight(convertResult.height!);

      // Step 2: Generate tiles
      const tileResult: any = await api.floorPlan.tile(
        convertResult.pngPath!,
        0, // No rotation during tiling - handled in Cesium
      );

      if (!tileResult.success) {
        throw new Error(tileResult.error || 'Tiling failed');
      }

      setTileCache(tileResult.cacheHash!);
      setTileMetadata(tileResult.metadata);

      setStep('complete');
    } catch (err) {
      setError((err as Error).message);
      setStep('info');
    } finally {
      setIsProcessing(false);
    }
  };

  // Complete and add the floor plan
  const handleComplete = useCallback(() => {
    if (!tileCache || !tileMetadata) return;

    // Create default transform centered on the map
    const center = defaultCenter || { lng: 0, lat: 0 };
    const transform: FloorPlanTransform = editingFloorPlan?.transform || createDefaultTransform(
      center.lng,
      center.lat,
      0.05 // Default 5cm per pixel
    );

    const overlay: Omit<FloorPlanOverlay, 'id' | 'createdAt' | 'updatedAt'> = {
      name: floorName || 'Floor Plan',
      floorNumber,
      sourcePath: filePath,
      sourceType: fileType === 'pdf' ? 'pdf' : 'image',
      sourcePage: fileType === 'pdf' ? selectedPage : undefined,
      tileCache,
      tileMetadata,
      imageWidth,
      imageHeight,
      transform,
      visible: true,
      opacity: 0.8,
    };

    onAdd(overlay);
    onClose();
  }, [
    tileCache,
    tileMetadata,
    defaultCenter,
    editingFloorPlan,
    floorName,
    floorNumber,
    filePath,
    fileType,
    selectedPage,
    imageWidth,
    imageHeight,
    onAdd,
    onClose,
  ]);

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 'select':
        return (
          <div className="modal__body">
            <div className="form-group">
              <label className="form-label">Floor Plan File</label>
              <div className="file-picker">
                <input
                  type="text"
                  className="file-input"
                  value={fileName}
                  placeholder="Select a PDF or image file..."
                  readOnly
                  onClick={handleSelectFile}
                />
                <button type="button" className="button button--secondary" onClick={handleSelectFile}>
                  Browse
                </button>
              </div>
              <p className="form-hint">Supported formats: PDF, PNG, JPEG</p>
            </div>

            {fileType === 'pdf' && pdfPageCount > 1 && (
              <div className="form-group">
                <label className="form-label">PDF Page</label>
                <select
                  className="form-select"
                  value={selectedPage}
                  onChange={(e) => setSelectedPage(parseInt(e.target.value))}
                >
                  {Array.from({ length: pdfPageCount }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      Page {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {error && <div className="form-error">{error}</div>}

            <div className="modal__actions">
              <button type="button" className="button button--secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="button button--primary"
                disabled={!filePath}
                onClick={() => setStep('info')}
              >
                Next
              </button>
            </div>
          </div>
        );

      case 'info':
        return (
          <div className="modal__body">
            <div className="form-group">
              <label className="form-label">Floor Name</label>
              <input
                type="text"
                className="form-input"
                value={floorName}
                onChange={(e) => setFloorName(e.target.value)}
                placeholder="e.g., Ground Floor, Basement, Floor 2"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Floor Number</label>
              <input
                type="number"
                className="form-input"
                value={floorNumber}
                onChange={(e) => setFloorNumber(parseInt(e.target.value) || 0)}
                placeholder="0 for ground, -1 for basement, 1+ for upper floors"
              />
              <p className="form-hint">Used for sorting: -1=Basement, 0=Ground, 1+=Upper floors</p>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={makeTransparent}
                  onChange={(e) => setMakeTransparent(e.target.checked)}
                />
                <span>Make white background transparent</span>
              </label>
              <p className="form-hint">Removes white/near-white background for better overlay visibility</p>
            </div>

            {error && <div className="form-error">{error}</div>}

            <div className="modal__actions">
              <button type="button" className="button button--secondary" onClick={() => setStep('select')}>
                Back
              </button>
              <button
                type="button"
                className="button button--primary"
                disabled={!floorName.trim()}
                onClick={handleProcess}
              >
                Process Floor Plan
              </button>
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className="modal__body processing-step">
            <div className="processing-spinner" />
            <div className="processing-stage">{progress.stage || 'Processing...'}</div>
            <div className="processing-message">{progress.message}</div>
            <div className="processing-bar">
              <div
                className="processing-bar-fill"
                style={{ width: `${progress.progress}%` }}
              />
            </div>
            <div className="processing-percent">{Math.round(progress.progress)}%</div>
          </div>
        );

      case 'complete':
        return (
          <div className="modal__body complete-step">
            <div className="complete-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                <path d="M22 4L12 14.01l-3-3" />
              </svg>
            </div>
            <div className="complete-title">Floor Plan Ready</div>
            <div className="complete-message">
              Click "Add to Map" to place the floor plan. You'll be able to position, scale,
              and rotate it to align with your building.
            </div>

            <div className="complete-summary">
              <div className="summary-item">
                <span className="summary-label">Name:</span>
                <span className="summary-value">{floorName}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Size:</span>
                <span className="summary-value">{imageWidth} x {imageHeight} px</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Tiles:</span>
                <span className="summary-value">{tileMetadata?.tileCount || 0} tiles</span>
              </div>
            </div>

            <div className="modal__actions">
              <button type="button" className="button button--secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="button button--primary"
                onClick={handleComplete}
              >
                Add to Map
              </button>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !isProcessing && onClose()}>
      <div className="modal floor-plan-modal">
        <div className="modal__header">
          <h2 className="modal__title">
            {isEditing ? 'Edit Floor Plan' : 'Add Floor Plan'}
          </h2>
          {!isProcessing && (
            <button className="modal__close" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Step indicator */}
        <div className="wizard-steps">
          <div className={`wizard-step ${step === 'select' ? 'active' : 'completed'}`}>
            <div className="wizard-step-number">1</div>
            <div className="wizard-step-label">Select</div>
          </div>
          <div className="wizard-step-line" />
          <div className={`wizard-step ${step === 'info' ? 'active' : step === 'processing' || step === 'complete' ? 'completed' : ''}`}>
            <div className="wizard-step-number">2</div>
            <div className="wizard-step-label">Info</div>
          </div>
          <div className="wizard-step-line" />
          <div className={`wizard-step ${step === 'processing' ? 'active' : step === 'complete' ? 'completed' : ''}`}>
            <div className="wizard-step-number">3</div>
            <div className="wizard-step-label">Process</div>
          </div>
          <div className="wizard-step-line" />
          <div className={`wizard-step ${step === 'complete' ? 'active' : ''}`}>
            <div className="wizard-step-number">4</div>
            <div className="wizard-step-label">Done</div>
          </div>
        </div>

        {renderStepContent()}
      </div>
    </div>
  );
}
